import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { CheckCircle2, Package, AlertTriangle, Truck, Clock, ChevronLeft, ShieldCheck, FileText, Download, Star } from "lucide-react";
import { haptic } from "@/lib/utils";

const STATUS_LABEL = {
  escrow_paid: "Paiement séquestré",
  shipped: "Expédié",
  released: "Fonds libérés",
  disputed: "En litige",
  refunded: "Remboursé",
};
const STATUS_COLOR = {
  escrow_paid: "pill-mint",
  shipped: "pill",
  released: "pill-mint",
  disputed: "pill-coral",
  refunded: "pill-coral",
};

export default function OrderDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [tracking, setTracking] = useState("");
  const [showReview, setShowReview] = useState(false);
  const [reviewHidden, setReviewHidden] = useState(false);
  const [reviewRating, setReviewRating] = useState(5);
  const [reviewComment, setReviewComment] = useState("");

  const submitOrderReview = async () => {
    try {
      await api.post("/reviews", { seller_id: order.seller_ids?.[0], rating: reviewRating, comment: reviewComment });
      localStorage.setItem(`reviewed_${id}`, "1");
      setShowReview(false);
      setReviewHidden(true);
      toast.success("Merci pour ton avis ⭐");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const dismissReview = () => {
    localStorage.setItem(`reviewed_${id}`, "1");
    setShowReview(false);
    setReviewHidden(true);
  };
  const [disputeReason, setDisputeReason] = useState("");
  const [showDispute, setShowDispute] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
    } catch { toast.error("Commande introuvable"); navigate("/dashboard"); }
  };

  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [id, user]);

  if (!user) return <div className="p-20 text-center">Connexion requise</div>;
  if (!order) return <div className="p-20 text-center">Chargement…</div>;

  const isBuyer = user.user_id === order.buyer_id;
  const isSeller = order.seller_ids?.includes(user.user_id);
  const hasValidLabel = !!(order.label_url && !order.label_url.includes("mockrelay"));

  const markShipped = async () => {
    setLoading(true);
    try {
      await api.post(`/orders/${id}/ship`, { tracking_number: tracking || undefined });
      toast.success("Commande marquée comme expédiée ✓");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const generateLabel = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/orders/${id}/label`);
      toast.success("Bordereau Mondial Relay généré ✓");
      if (data.label_url) window.open(data.label_url, "_blank");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur lors de la génération du bordereau"); }
    finally { setLoading(false); }
  };

  const confirmReceipt = async () => {
    if (!window.confirm("Confirmer la réception ? Les fonds seront libérés au vendeur.")) return;
    setLoading(true);
    try {
      await api.post(`/orders/${id}/confirm-receipt`);
      toast.success("Réception confirmée. Merci !");
      setShowReview(true);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  const openDispute = async () => {
    if (!disputeReason.trim()) { toast.error("Décris le problème"); return; }
    setLoading(true);
    try {
      await api.post(`/orders/${id}/dispute`, { reason: disputeReason });
      toast.success("Litige ouvert. Fripri reviendra vers toi sous 48h.");
      setShowDispute(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
    finally { setLoading(false); }
  };

  // Timeline steps
  const steps = [
    { key: "escrow_paid", label: "Paiement", icon: ShieldCheck, done: true, date: order.created_at },
    { key: "shipped", label: "Expédié", icon: Truck, done: ["shipped", "released", "escrow_received"].includes(order.status), date: order.shipped_at },
    { key: "released", label: "Reçu & libéré", icon: CheckCircle2, done: order.status === "released", date: order.released_at },
  ];

  return (
    <div className="max-w-4xl mx-auto px-4 lg:px-8 py-6 lg:py-10 fade-in has-bottom-nav" data-testid="order-detail-page">
      <Link to="/dashboard" className="label font-bold inline-flex items-center gap-1 mb-4"><ChevronLeft size={14}/> Mon espace</Link>
      <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
        <div>
          <p className="label mb-2">Commande #{order.order_id.slice(-8).toUpperCase()}</p>
          <h1 className="text-3xl lg:text-4xl font-black tracking-tighter">{isBuyer ? "Mon achat" : "Ma vente"}</h1>
          <p className="text-sm text-[color:var(--color-text-muted)] mt-2">{new Date(order.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</p>
        </div>
        <span className={`pill ${STATUS_COLOR[order.status] || ""} text-xs`}>{STATUS_LABEL[order.status] || order.status}</span>
      </div>

      {/* Timeline */}
      <div className="mb-8 p-4 lg:p-6 border-2 border-black bg-white">
        <p className="label font-bold mb-4">Suivi</p>
        <div className="flex items-start justify-between gap-2 relative">
          <div className="absolute top-5 left-5 right-5 h-0.5 bg-black z-0" />
          {steps.map((s, i) => (
            <div key={s.key} className="flex flex-col items-center text-center flex-1 z-10">
              <div className={`w-10 h-10 border-2 border-black flex items-center justify-center ${s.done ? 'bg-[color:var(--color-mint)]' : 'bg-white'}`}>
                <s.icon size={18} strokeWidth={2.5} />
              </div>
              <p className="label mt-2">{s.label}</p>
              {s.date && <p className="text-[10px] text-[color:var(--color-text-muted)] mt-0.5">{new Date(s.date).toLocaleDateString("fr-FR", {day:"2-digit", month:"short"})}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Products */}
      <div className="mb-8">
        <p className="label font-bold mb-3">Articles ({order.products?.length || 0})</p>
        <div className="space-y-3">
          {(order.products || []).map(p => (
            <Link key={p.product_id} to={`/produit/${p.product_id}`} className="flex gap-3 p-3 border-2 border-black bg-white card-hover">
              <div className="w-20 h-20 shrink-0 overflow-hidden bg-[color:var(--color-surface)]">
                {p.images?.[0] && <img src={fileUrl(p.images[0])} className="w-full h-full object-cover" alt=""/>}
              </div>
              <div className="flex-1 min-w-0">
                <p className="label">{p.brand}</p>
                <p className="font-bold truncate">{p.title}</p>
                <p className="font-black mt-1" style={{color:"var(--color-coral)"}}>{p.price?.toFixed(2)}€</p>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Money breakdown */}
      <div className="mb-8 p-4 lg:p-6 border-2 border-black bg-[color:var(--color-surface)]">
        <p className="label font-bold mb-3">Détail</p>
        <div className="space-y-1 text-sm">
          {(order.shipping_fee > 0 || order.buyer_fee > 0) && (
            <div className="flex justify-between"><span>Articles</span><span className="font-bold">{order.item_total?.toFixed(2)}€</span></div>
          )}
          {order.shipping_fee > 0 && (
            <div className="flex justify-between"><span>Livraison Mondial Relay</span><span className="font-bold">{order.shipping_fee?.toFixed(2)}€</span></div>
          )}
          {order.buyer_fee > 0 && (
            <div className="flex justify-between"><span>Protection acheteur</span><span className="font-bold">{order.buyer_fee?.toFixed(2)}€</span></div>
          )}
          <div className="flex justify-between"><span>Montant payé</span><span className="font-bold">{order.amount?.toFixed(2)}€</span></div>
          {isSeller && (
            <>
              {order.buyer_fee > 0 ? (
                <div className="flex justify-between text-base mt-2 pt-2 border-t border-black"><span className="font-bold">Tu reçois (100% de tes articles)</span><span className="font-black" style={{color:"var(--color-coral)"}}>{order.seller_payout?.toFixed(2)}€</span></div>
              ) : (
                <>
                  <div className="flex justify-between"><span>Commission Fripri</span><span>−{order.commission_amount?.toFixed(2)}€</span></div>
                  <div className="flex justify-between text-base mt-2 pt-2 border-t border-black"><span className="font-bold">Tu reçois</span><span className="font-black" style={{color:"var(--color-coral)"}}>{order.seller_payout?.toFixed(2)}€</span></div>
                </>
              )}
            </>
          )}
        </div>
        {order.delivery_status === "delivered" && (
          <div className="mt-4 pt-4 border-t border-black">
            <p className="text-sm font-bold" data-testid="delivered-info">📦 Colis livré{order.delivered_at ? ` le ${new Date(order.delivered_at).toLocaleDateString("fr-FR")}` : ""}</p>
          </div>
        )}
        {order.tracking_number && (
          <div className="mt-4 pt-4 border-t border-black">
            <p className="label mb-1">N° de suivi</p>
            <p className="font-mono text-sm font-bold">{order.tracking_number}</p>
          </div>
        )}
      </div>

      {/* Actions */}
      {order.status === "escrow_paid" && isSeller && (
        <div className="p-4 lg:p-6 border-2 border-black bg-[color:var(--color-mint)] mb-6" data-testid="action-ship-box">
          <h3 className="font-black text-lg mb-2">Prépare l'envoi</h3>
          <p className="text-sm mb-4">1. Génère ton bordereau Mondial Relay et imprime-le. 2. Dépose ton colis en point relais. 3. Marque la commande comme expédiée.</p>
          {hasValidLabel ? (
            <a href={order.label_url} target="_blank" rel="noreferrer" className="btn btn-primary w-full mb-3" data-testid="download-label-btn">
              <Download size={16} strokeWidth={2.5}/> Télécharger le bordereau (PDF)
            </a>
          ) : (
            <button onClick={generateLabel} disabled={loading} className="btn btn-primary w-full mb-3" data-testid="generate-label-btn">
              <FileText size={16} strokeWidth={2.5}/> {loading ? "Génération…" : "Générer mon bordereau Mondial Relay"}
            </button>
          )}
          <input className="field mb-3" placeholder="N° de suivi (auto-rempli par le bordereau, ou saisis-le)" value={tracking} onChange={e=>setTracking(e.target.value)} data-testid="tracking-input"/>
          <button onClick={markShipped} disabled={loading} className="btn btn-ghost bg-white w-full" data-testid="mark-shipped-btn">
            <Truck size={16} strokeWidth={2.5}/> Marquer comme expédié
          </button>
        </div>
      )}

      {order.status === "shipped" && isSeller && (
        <div className="p-4 lg:p-6 border-2 border-black bg-white mb-6" data-testid="seller-label-box">
          <h3 className="font-black text-lg mb-2">Bordereau Mondial Relay</h3>
          {hasValidLabel ? (
            <a href={order.label_url} target="_blank" rel="noreferrer" className="btn btn-primary w-full" data-testid="download-label-btn-shipped">
              <Download size={16} strokeWidth={2.5}/> Télécharger le bordereau (PDF)
            </a>
          ) : (
            <>
              <p className="text-sm mb-3">Ton bordereau n'a pas encore été généré. Clique ci-dessous pour l'obtenir.</p>
              <button onClick={generateLabel} disabled={loading} className="btn btn-primary w-full" data-testid="generate-label-btn-shipped">
                <FileText size={16} strokeWidth={2.5}/> {loading ? "Génération…" : "Générer mon bordereau Mondial Relay"}
              </button>
            </>
          )}
        </div>
      )}

      {!reviewHidden && (showReview || (order.status === "released" && isBuyer && !localStorage.getItem(`reviewed_${id}`))) && (
        <div className="p-4 lg:p-6 border-2 border-black bg-[color:var(--color-mint)] mb-6" data-testid="review-invite-box">
          <h3 className="font-black text-lg mb-1">Note ton vendeur ⭐</h3>
          <p className="text-sm mb-3">Ton avis aide toute la communauté Fripri et récompense les bons vendeurs.</p>
          <div className="flex gap-1 mb-3" data-testid="review-stars">
            {[1,2,3,4,5].map(n => (
              <button key={n} type="button" onClick={() => { haptic(); setReviewRating(n); }} data-testid={`review-star-${n}`} aria-label={`${n} étoiles`}>
                <Star size={28} strokeWidth={2} fill={n <= reviewRating ? "#FFE600" : "transparent"} className="text-black"/>
              </button>
            ))}
          </div>
          <textarea className="field mb-3 w-full" rows={2} placeholder="Un petit mot sur ta commande ? (optionnel)" value={reviewComment} onChange={e=>setReviewComment(e.target.value)} data-testid="review-comment-input"/>
          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={submitOrderReview} className="btn btn-primary flex-1" data-testid="submit-review-btn">Publier mon avis</button>
            <button onClick={dismissReview} className="btn btn-ghost bg-white" data-testid="dismiss-review-btn">Plus tard</button>
          </div>
        </div>
      )}

      {(order.status === "shipped" || order.status === "escrow_paid") && isBuyer && (
        <div className="p-4 lg:p-6 border-2 border-black bg-[color:var(--color-yellow)] mb-6" data-testid="action-confirm-box">
          <h3 className="font-black text-lg mb-2">Confirme ta réception</h3>
          <p className="text-sm mb-4">L'article est conforme à la description ? Confirme et les fonds seront libérés au vendeur. Sans action, libération auto après 7 jours.</p>
          <div className="flex flex-col sm:flex-row gap-2">
            <button onClick={confirmReceipt} disabled={loading} className="btn btn-primary flex-1" data-testid="confirm-receipt-btn">
              <CheckCircle2 size={16} strokeWidth={2.5}/> Tout est OK, libérer les fonds
            </button>
            <button onClick={()=>setShowDispute(true)} className="btn btn-ghost" data-testid="open-dispute-btn">
              <AlertTriangle size={16} strokeWidth={2.5}/> Signaler un problème
            </button>
          </div>
        </div>
      )}

      {showDispute && (
        <div className="p-4 lg:p-6 border-2 border-black bg-[color:var(--color-coral)] text-white mb-6">
          <h3 className="font-black text-lg mb-3">Décris le problème</h3>
          <textarea rows="4" className="field text-black mb-3" placeholder="Article non conforme, abîmé, manquant…" value={disputeReason} onChange={e=>setDisputeReason(e.target.value)} data-testid="dispute-reason"/>
          <div className="flex gap-2">
            <button onClick={openDispute} disabled={loading} className="btn bg-black text-white border-black flex-1" data-testid="submit-dispute-btn">Ouvrir un litige</button>
            <button onClick={()=>setShowDispute(false)} className="btn btn-ghost">Annuler</button>
          </div>
        </div>
      )}

      {order.status === "disputed" && (
        <div className="p-4 border-2 border-black bg-[color:var(--color-coral)] text-white mb-6 flex items-center gap-3">
          <AlertTriangle size={24} strokeWidth={2.5}/>
          <div className="flex-1">
            <p className="font-bold">Litige en cours</p>
            <p className="text-sm opacity-90">{order.dispute_reason || "Fripri examine cette commande."}</p>
          </div>
        </div>
      )}

      {order.status === "released" && (
        <div className="p-4 border-2 border-black bg-[color:var(--color-mint)] mb-6 flex items-center gap-3">
          <CheckCircle2 size={24} strokeWidth={2.5}/>
          <div className="flex-1">
            <p className="font-bold">Transaction terminée ✓</p>
            <p className="text-sm">{isSeller ? `${order.seller_payout?.toFixed(2)}€ ont été crédités sur ton solde.` : "Merci d'avoir choisi Fripri."}</p>
          </div>
        </div>
      )}
    </div>
  );
}
